import { AlertTriangle, HelpCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'

type Variant = 'default' | 'destructive' | 'warning'

const VARIANT_ICON = {
  default: HelpCircle,
  warning: AlertTriangle,
  destructive: Trash2,
} as const

const VARIANT_ICON_COLOR = {
  default: 'text-primary',
  warning: 'text-amber-500',
  destructive: 'text-destructive',
} as const

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'default',
  onConfirm,
  onCancel,
  isLoading = false,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: Variant
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}) {
  if (!open) return null

  const Icon = VARIANT_ICON[variant]
  const iconColor = VARIANT_ICON_COLOR[variant]
  const confirmVariant = variant === 'destructive' ? 'destructive' : 'default'

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-md mx-4 animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-muted ${iconColor}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="confirm-title" className="text-base font-semibold leading-tight">{title}</h2>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{message}</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>{cancelLabel}</Button>
            <Button variant={confirmVariant} onClick={onConfirm} isLoading={isLoading}>{confirmLabel}</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
